import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductSummaryBarRefs
 * @property {HTMLElement} summaryBar - The sticky summary bar inner container
 */

/**
 * A custom element that manages a sticky product summary bar (thumbnail + price)
 * pinned directly under the sticky header on mobile.
 *
 * It becomes visible once the main product price block scrolls out of view and
 * keeps its thumbnail and price in sync with variant changes (via `variant:update`
 * + morph, mirroring the sticky add-to-cart behavior).
 *
 * @extends {Component<ProductSummaryBarRefs>}
 */
class ProductSummaryBar extends Component {
  requiredRefs = ['summaryBar'];

  /** @type {IntersectionObserver | null} */
  #priceObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #isStuck = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupObserver();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Keep the bar flush against the header if the header height changes
    // (e.g. announcement bar collapsing, orientation change).
    window.addEventListener('resize', this.#positionBar, { signal, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#priceObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Finds the main product price block used as the scroll sentinel.
   * @returns {Element | null}
   */
  #getPriceSentinel() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    // The main price block renders as a <product-price> element inside product details.
    return section.querySelector('.product-details product-price');
  }

  /**
   * Reads the current sticky-header height so the trigger can fire the moment
   * the price slips *behind the header*, not only once it clears the viewport top.
   * @returns {number}
   */
  #getHeaderOffset() {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;

    const header = document.querySelector('header-component');
    if (header instanceof HTMLElement && header.offsetHeight > 0) return header.offsetHeight;

    return 60;
  }

  /**
   * Sets up the IntersectionObserver watching the main price block visibility.
   * The observer root is shrunk from the top by the header height so the bar
   * appears immediately when the price disappears under the sticky header.
   */
  #setupObserver() {
    const sentinel = this.#getPriceSentinel();
    if (!sentinel) return;

    const headerOffset = this.#getHeaderOffset();

    this.#priceObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (!entry.isIntersecting) {
          // Show only when the price has scrolled up behind the header,
          // not when it hasn't been reached yet (still below the viewport).
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom <= headerOffset) {
            this.#show();
          } else {
            this.#hide();
          }
        } else {
          this.#hide();
        }
      },
      {
        // Pull the root's top edge down to sit just under the sticky header.
        rootMargin: `-${Math.round(headerOffset)}px 0px 0px 0px`,
      }
    );

    this.#priceObserver.observe(sentinel);
  }

  /**
   * Pins the bar flush against the *actual* bottom edge of the sticky header.
   *
   * The `--header-height` variable also counts the announcement bar, which has
   * already scrolled away by the time this bar appears — relying on it leaves a
   * gap that reveals the page content underneath. Measuring the header's live
   * `getBoundingClientRect().bottom` keeps the bar seamlessly attached.
   */
  #positionBar = () => {
    const header = document.querySelector('header-component');
    let top = this.#getHeaderOffset();

    if (header instanceof HTMLElement) {
      const bottom = header.getBoundingClientRect().bottom;
      // Clamp to >=0 so it never floats above the viewport on overscroll.
      if (bottom >= 0) top = bottom;
    }

    this.refs.summaryBar.style.top = `${Math.round(top)}px`;
  };

  #show() {
    this.#isStuck = true;
    this.#positionBar();
    this.refs.summaryBar.dataset.visible = 'true';
  }

  #hide() {
    this.#isStuck = false;
    this.refs.summaryBar.dataset.visible = 'false';
  }

  /**
   * Opens the image preview when the summary thumbnail is tapped.
   *
   * Reuses the product gallery's zoom dialog when zoom is enabled; otherwise
   * gracefully scrolls the gallery back into view so the shopper can still
   * inspect the imagery.
   * @param {PointerEvent} event - The click event.
   */
  handlePreview = (event) => {
    const section = this.closest('.shopify-section');
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (
      section?.querySelector('media-gallery') ?? null
    );
    if (!gallery) return;

    // Open the zoom dialog when available (gated by the gallery's `zoom` setting).
    if (gallery.refs?.zoomDialogComponent) {
      const index = Number(gallery.slideshow?.current) || 0;

      // The zoom dialog opens each image at 1.5x by default (built for "zoom to
      // inspect"). For a summary-bar preview we want the whole image in view, so
      // reset every drag-zoom wrapper to fit (1x, centered) before opening. This
      // is scoped to the preview path; tapping a gallery image still opens at 1.5x,
      // and closing the dialog restores the 1.5x default via the wrapper's own reset.
      const zoomDialog = gallery.refs.zoomDialogComponent;
      const wrappers = zoomDialog.querySelectorAll?.('drag-zoom-wrapper') ?? [];
      for (const wrapper of wrappers) {
        if (typeof wrapper.resetToFit === 'function') wrapper.resetToFit();
      }

      gallery.zoom(index, event);
      return;
    }

    // Fallback: bring the gallery back into view.
    gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * Handles variant update events by morphing the new server-rendered bar content,
   * keeping the thumbnail and price in sync with the selected variant.
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newBar = event.detail.data.html.querySelector('product-summary-bar');
    if (!newBar) return;

    const newInner = newBar.querySelector('[ref="summaryBar"]');
    if (!newInner) return;

    // Preserve current visibility across the morph.
    const currentVisible = this.refs.summaryBar.dataset.visible || 'false';
    morph(this.refs.summaryBar, newInner, { childrenOnly: true });
    this.refs.summaryBar.dataset.visible = currentVisible;

    // Belt-and-suspenders: sync the thumbnail to the exact image the gallery
    // shows, straight from the variant's featured media. Guards against cases
    // where the re-rendered markup fell back to the product's default image.
    this.#syncThumbnail(event.detail.resource);
  };

  /**
   * Points the thumbnail at the variant's featured-media preview image so it
   * always matches the gallery. No-op when the variant has no dedicated media.
   * @param {{ featured_media?: { preview_image?: { src?: string } } } | null} variant
   */
  #syncThumbnail(variant) {
    const src = variant?.featured_media?.preview_image?.src;
    if (!src) return;

    const img = this.refs.summaryBar.querySelector('.product-summary-bar__image-img');
    if (!(img instanceof HTMLImageElement)) return;

    // Request a thumbnail-sized asset from the Shopify CDN rather than the full-res source.
    let thumbSrc = src;
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '120');
      thumbSrc = url.href;
    } catch {
      // Keep the original src if URL parsing fails.
    }

    if (img.src !== thumbSrc) {
      img.src = thumbSrc;
      img.removeAttribute('srcset');
    }
  }
}

if (!customElements.get('product-summary-bar')) {
  customElements.define('product-summary-bar', ProductSummaryBar);
}
